import { useTranslation } from 'react-i18next';
import { FeatureGroup, LayersControl } from 'react-leaflet';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import GeofenceLayer, {
  FillHoverOpacity,
  FillRegularOpacity,
  GeofenceLayerProps,
} from '../geofence-layer/GeofenceLayer';
import { ComponentType, useMemo } from 'react';
import { Path } from 'leaflet';
import { GeofenceProperties } from '@casops/store/apis/geofencesApi';

const eventHandlers = {
  mouseover: ({ propagatedFrom }: L.LeafletMouseEvent) => {
    if (propagatedFrom instanceof Path) {
      propagatedFrom.setStyle({ fillOpacity: FillHoverOpacity });
    }
  },
  mouseout: ({ propagatedFrom }: L.LeafletMouseEvent) => {
    if (propagatedFrom instanceof Path) {
      propagatedFrom.setStyle({ fillOpacity: FillRegularOpacity });
    }
  },
};

export interface GeofenceLayerGroupProps {
  LayerComponent?: ComponentType<GeofenceLayerProps>;
  layerNameTrans?: string;
  checked?: boolean;
  geoJson?: FeatureCollection<Geometry, GeofenceProperties>;
  filter?: (feature: Feature<Geometry, GeofenceProperties>) => boolean;
  pathOptions?: L.PathOptions;
}

export function GeofenceLayerGroup({
  layerNameTrans,
  checked = true,
  geoJson,
  filter,
  pathOptions,
  LayerComponent = GeofenceLayer,
}: GeofenceLayerGroupProps) {
  const { t } = useTranslation();

  // Optimise and persist filtering to handle thousands of geofences.
  const filteredFeatures = useMemo<Feature<Geometry, GeofenceProperties>[]>(() => {
    if (!geoJson) return [];
    if (filter === undefined) return geoJson.features;
    return geoJson.features.filter(filter);
  }, [geoJson, filter]);

  if (geoJson === undefined) {
    return null;
  }

  const controlName = layerNameTrans ?? 'Model.Plural.Geofence_other';

  return (
    <LayersControl.Overlay
      key={controlName} // The key must change for control name changes to be rendered.
      name={t(controlName)}
      checked={checked}
    >
      <FeatureGroup eventHandlers={eventHandlers}>
        {filteredFeatures.map((feature, index) => {
          return <LayerComponent key={index} feature={feature} pathOptions={pathOptions} />;
        })}
      </FeatureGroup>
    </LayersControl.Overlay>
  );
}

export default GeofenceLayerGroup;
